<template>
  <div
    :class="`col-md-${column.width || 12}`"
    class="builder-column-wrapper"
  >
    <div
      class="builder-column"
      :style="columnStyles"
      :class="{ 'selected': isSelected, 'drag-over': isDragOver }"
      draggable="true"
      @dragstart="handleColumnDragStart"
      @dragend="handleColumnDragEnd"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @click.stop="handleSelect"
    >
      <div class="column-toolbar">
        <div class="toolbar-group">
          <span class="drag-handle" title="Drag to reorder">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 4h12v1H2V4zm0 3.5h12v1H2v-1zm0 3.5h12v1H2v-1z"/>
            </svg>
          </span>
          <div class="size-dropdown">
            <button type="button" class="size-btn" @click.stop="showSizeMenu = !showSizeMenu" :title="`Width: ${widthLabel}`">
              {{ widthLabel }}
            </button>
            <div v-if="showSizeMenu" class="size-menu" @click.stop>
              <button v-for="size in columnSizes" :key="size.value" type="button" class="size-option" :class="{ active: column.width === size.value }" @click="handleSetWidth(size.value)">
                {{ size.label }}
              </button>
            </div>
          </div>
          <button type="button" class="icon-btn" title="Edit Column" @click.stop="handleEdit">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175l-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
            </svg>
          </button>
          <button type="button" class="icon-btn" title="Copy Column" @click.stop="handleCopy">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6z"/>
              <path d="M2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1H2z"/>
            </svg>
          </button>
          <button type="button" class="icon-btn" title="Save Column" @click.stop="handleSave">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
            </svg>
          </button>
          <button type="button" class="icon-btn icon-btn-danger" title="Delete Column" @click.stop="handleDelete">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
              <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="column-content">
        <div v-if="column.elements.length > 0" class="elements-list">
          <BuilderElement
            v-for="(element, index) in column.elements"
            :key="element.uid"
            :component="element"
            :element-index="index"
            :column-uid="column.uid"
            :selected-uid="selectedUid"
            @select="$emit('select', $event)"
            @edit="$emit('edit', $event)"
            @update="handleElementUpdate"
            @delete="handleDeleteElement"
            @copy="handleCopyElement"
            @save="handleSaveElement"
            @reorder="handleReorderElement"
            @move-to-column="handleMoveToColumn"
          />
        </div>

        <div v-else class="empty-column">
          <p class="text-muted mb-0">Empty column</p>
        </div>
      </div>

      <div class="add-element-section">
        <button
          type="button"
          class="add-element-btn"
          @click.stop="showElementMenu = !showElementMenu"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
          </svg>
          Element
        </button>

        <div v-if="showElementMenu" class="element-menu" @click.stop>
          <div class="element-menu-header">
            <strong>Add Element</strong>
            <button
              type="button"
              class="btn-close btn-sm"
              @click="showElementMenu = false"
            ></button>
          </div>
          <div class="element-menu-body">
            <button
              v-for="elementType in elementTypes"
              :key="elementType.value"
              type="button"
              class="element-option"
              @click="handleAddElement(elementType.value)"
            >
              {{ elementType.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Column, Element, ElementType } from '../../types/form-builder';
import { applyInlineStyles, createElement } from '../../utils/form-helpers';
import { saveComponent } from '../../utils/storage';
import BuilderElement from './BuilderElement.vue';

interface Props {
  column: Column;
  columnIndex: number;
  containerUid: string;
  selectedUid?: string;
}

interface Emits {
  (e: 'select', uid: string): void;
  (e: 'edit', uid: string): void;
  (e: 'update', column: Column): void;
  (e: 'delete', uid: string): void;
  (e: 'copy', column: Column): void;
  (e: 'save', column: Column): void;
  (e: 'reorderColumn', fromIndex: number, toIndex: number): void;
  (e: 'moveElement', data: { element: Element; fromColumnUid: string; toColumnUid: string; toIndex: number }): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const showElementMenu = ref(false);
const showSizeMenu = ref(false);
const isDragOver = ref(false);

const columnSizes = [
  { label: 'Full', value: 12 },
  { label: '11/12', value: 11 },
  { label: '5/6', value: 10 },
  { label: '3/4', value: 9 },
  { label: '2/3', value: 8 },
  { label: '7/12', value: 7 },
  { label: '1/2', value: 6 },
  { label: '5/12', value: 5 },
  { label: '1/3', value: 4 },
  { label: '1/4', value: 3 },
  { label: '1/6', value: 2 },
  { label: '1/12', value: 1 },
];

const widthLabel = computed(() => {
  const w = props.column.width || 12;
  const labels: Record<number, string> = {
    12: 'Full',
    11: '11/12',
    10: '5/6',
    9: '3/4',
    8: '2/3',
    7: '7/12',
    6: '1/2',
    5: '5/12',
    4: '1/3',
    3: '1/4',
    2: '1/6',
    1: '1/12',
  };
  return labels[w] || `${w}/12`;
});

const elementTypes = [
  { label: 'Text Input', value: 'text' as ElementType },
  { label: 'Email', value: 'email' as ElementType },
  { label: 'Number', value: 'number' as ElementType },
  { label: 'Text Area', value: 'textarea' as ElementType },
  { label: 'Select', value: 'select' as ElementType },
  { label: 'Checkbox', value: 'checkbox' as ElementType },
  { label: 'Radio', value: 'radio' as ElementType },
  { label: 'Date', value: 'date' as ElementType },
  { label: 'File', value: 'file' as ElementType },
  { label: 'Heading', value: 'heading' as ElementType },
  { label: 'Paragraph', value: 'paragraph' as ElementType },
  { label: 'Button', value: 'button' as ElementType },
];

const isSelected = computed(() => props.selectedUid === props.column.uid);

const columnStyles = computed(() => applyInlineStyles(props.column.design));

function handleSelect() {
  emit('select', props.column.uid);
}

function handleEdit() {
  emit('edit', props.column.uid);
}

function handleCopy() {
  emit('copy', props.column);
}

function handleSave() {
  emit('save', props.column);
}

function handleSetWidth(width: number) {
  const updatedColumn = {
    ...props.column,
    width,
  };
  emit('update', updatedColumn);
  showSizeMenu.value = false;
}

function handleAddElement(elementType: ElementType) {
  const newElement = createElement(elementType);

  const updatedColumn = {
    ...props.column,
    elements: [...props.column.elements, newElement],
  };

  emit('update', updatedColumn);
  showElementMenu.value = false;
}

function handleElementUpdate(updatedElement: Element | Column) {
  const updatedColumn = {
    ...props.column,
    elements: props.column.elements.map((el) =>
      el.uid === updatedElement.uid ? updatedElement : el
    ),
  };

  emit('update', updatedColumn);
}

function handleDeleteElement(uid: string) {
  const updatedColumn = {
    ...props.column,
    elements: props.column.elements.filter((el) => el.uid !== uid),
  };

  emit('update', updatedColumn);
}

function handleCopyElement(element: Element) {
  const newElement = {
    ...JSON.parse(JSON.stringify(element)),
    uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };

  const updatedColumn = {
    ...props.column,
    elements: [...props.column.elements, newElement],
  };

  emit('update', updatedColumn);
}

function handleSaveElement(element: Element) {
  saveComponent(element as any).then(() => {
    alert('Element saved!');
  });
}

function handleReorderElement(fromIndex: number, toIndex: number) {
  const newElements = [...props.column.elements];
  const [moved] = newElements.splice(fromIndex, 1);
  newElements.splice(toIndex, 0, moved);

  const updatedColumn = {
    ...props.column,
    elements: newElements,
  };

  emit('update', updatedColumn);
}

function handleMoveToColumn(data: { element: Element; fromColumnUid: string; toIndex: number }) {
  emit('moveElement', {
    element: data.element,
    fromColumnUid: data.fromColumnUid,
    toColumnUid: props.column.uid,
    toIndex: data.toIndex
  });
}

function handleDelete() {
  emit('delete', props.column.uid);
}

function handleColumnDragStart(e: DragEvent) {
  e.stopPropagation();
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/column', JSON.stringify({
      type: 'column',
      index: props.columnIndex,
      containerUid: props.containerUid
    }));
  }
}

function handleColumnDragEnd() {
  isDragOver.value = false;
}

function handleDragOver(e: DragEvent) {
  const hasColumn = e.dataTransfer?.types.includes('application/column');
  const hasElement = e.dataTransfer?.types.includes('application/element');

  if (hasColumn || hasElement) {
    e.preventDefault();
    e.stopPropagation();
    isDragOver.value = true;
  }
}

function handleDragLeave(e: DragEvent) {
  const relatedTarget = e.relatedTarget as HTMLElement;
  if (!relatedTarget || !e.currentTarget || !(e.currentTarget as HTMLElement).contains(relatedTarget)) {
    isDragOver.value = false;
  }
}

function handleDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;

  const columnData = e.dataTransfer?.getData('application/column');
  if (columnData) {
    const { index: fromIndex, containerUid } = JSON.parse(columnData);
    if (containerUid === props.containerUid && fromIndex !== props.columnIndex) {
      emit('reorderColumn', fromIndex, props.columnIndex);
    }
    return;
  }

  const elementData = e.dataTransfer?.getData('application/element');
  if (elementData) {
    const { element, columnUid: fromColumnUid } = JSON.parse(elementData);
    if (fromColumnUid !== props.column.uid) {
      emit('moveElement', {
        element,
        fromColumnUid,
        toColumnUid: props.column.uid,
        toIndex: props.column.elements.length
      });
    }
  }
}
</script>

<style scoped>
.builder-column-wrapper {
  position: relative;
}

.builder-column {
  border: 2px dashed #20c997;
  padding: 10px;
  border-radius: 6px;
  min-height: 120px;
  position: relative;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
}

.builder-column:hover {
  border-color: #0dcaf0;
}

.builder-column.selected {
  border-color: #0dcaf0;
  border-style: solid;
  background-color: rgba(13, 202, 240, 0.05);
}

.builder-column.drag-over {
  border-color: #198754;
  background-color: rgba(25, 135, 84, 0.1);
}

.column-toolbar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 3px;
  background: #f8f9fa;
  padding: 3px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.drag-handle {
  cursor: grab;
  padding: 4px;
  color: #6c757d;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  color: #0dcaf0;
}

.size-dropdown {
  position: relative;
}

.size-btn {
  height: 24px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 3px;
  cursor: pointer;
  color: #495057;
}

.size-btn:hover {
  background-color: #e9ecef;
  border-color: #0dcaf0;
  color: #0dcaf0;
}

.size-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  min-width: 80px;
}

.size-option {
  display: block;
  width: 100%;
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.size-option:hover {
  background-color: #e9ecef;
}

.size-option.active {
  background-color: #0dcaf0;
  color: white;
}

.icon-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
  transition: all 0.15s;
}

.icon-btn:hover {
  background-color: #e9ecef;
  border-color: #0dcaf0;
  color: #0dcaf0;
}

.icon-btn-danger:hover {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

.column-content {
  flex: 1;
}

.elements-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.empty-column {
  padding: 20px;
  text-align: center;
  background-color: rgba(32, 201, 151, 0.05);
  border-radius: 4px;
}

.add-element-section {
  margin-top: 10px;
  position: relative;
}

.add-element-btn {
  width: 100%;
  padding: 8px 12px;
  font-size: 13px;
  border: 2px dashed #dee2e6;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #6c757d;
  transition: all 0.2s;
}

.add-element-btn:hover {
  border-color: #0dcaf0;
  color: #0dcaf0;
  background: rgba(13, 202, 240, 0.05);
}

.element-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
}

.element-menu-header {
  padding: 10px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-radius: 8px 8px 0 0;
  position: sticky;
  top: 0;
}

.element-menu-body {
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
}

.element-option {
  padding: 8px 12px;
  font-size: 12px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}

.element-option:hover {
  background-color: #e9ecef;
  border-color: #0dcaf0;
}
</style>
